// The multiplayer sync wire protocol (docs/multiplayer-sync/roadmap.md
// section 3.4): the three message shapes `useRelayConnection`'s `send()`/
// `messages` carry once Phase 2/3 start using them for real. The relay
// itself (`sstk-studio-infra`'s `relay.tf`/`relay/src/index.js`) fans out
// whatever JSON-serialisable `payload` a client sends, unvalidated and
// uninterpreted (roadmap section 2.1) - this module is the client-side
// discipline that makes that free-form `payload` a fixed, versioned
// contract instead, mirroring `layoutStorage.ts`'s
// `WORKSPACE_LAYOUT_SCHEMA_VERSION` pattern for the same reason: so a
// future breaking change to the shape doesn't silently desync an old
// client still holding a stale `SYNC_MESSAGE_SCHEMA_VERSION` against a new
// one.
//
// Nothing in this repo constructs or parses these yet - Phase 2 (transform
// broadcast) and Phase 3 (Logic host sync) are what actually send and
// consume them. This module only finalises and documents the shape itself,
// per roadmap Phase 1's own task list; it is a prepared extension point,
// the same posture `clientLogicRuntime.ts`'s `applyRemoteAction`/
// `'__remote'` took for Phase 0.
//
// Deliberately not a server-authoritative contract: per roadmap section
// 3.1, nothing validates a reported transform against physics, speed
// limits, or collision - `isSyncMessage` below only checks that a payload
// is *structurally* one of these three shapes at the version this client
// understands, the same "no simulation state, but a fixed shape" honesty
// the relay Lambda itself already applies to presence.
import type { LogicEffect } from '../../../engine/runtime/logic/evaluator';
import { QUEST_STATUSES, type QuestStatus } from '../../../engine/runtime/logic/contract';

/** Bumped whenever a `SyncMessage` variant's shape changes incompatibly.
 * `isSyncMessage` rejects anything at a different version rather than
 * guessing at forward/backward compatibility, so a client running an old
 * build simply ignores messages it can't understand instead of
 * misinterpreting them. */
export const SYNC_MESSAGE_SCHEMA_VERSION = 1;

interface SyncMessageBase {
  v: typeof SYNC_MESSAGE_SCHEMA_VERSION;
}

/** A client reporting its own owned entity's transform (roadmap 3.1) - the
 * local player's own position, or a host-driven NPC/prop it owns (3.2).
 * `entityId` is the reporting player's relay `playerId` for a player
 * transform, or an authored entity id
 * (`MapCharacterInstance.id`/a `liveSceneStore`-spawned id) for a
 * host-driven entity. `seq` is a monotonic counter the sender owns, so a
 * receiver can drop an out-of-order frame rather than rendering it. */
export interface TransformSyncMessage extends SyncMessageBase {
  kind: 'transform';
  entityId: string;
  seq: number;
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/** Host-only: broadcast on each rule firing that touches shared
 * (non-player-owned) Logic state (roadmap 3.2 item 2), so non-host clients
 * can apply the same effects via `applyRemoteAction` without independently
 * evaluating the rule themselves. `seq` is a monotonic counter the host
 * owns, shared with `LogicSnapshotSyncMessage` (both come from the same
 * broadcasting host, so one counter orders them together) - lets a receiver
 * drop a stale/already-applied message (`useMultiplayerSync.ts`'s own
 * dedup guard) the same way `TransformSyncMessage.seq` already does, so a
 * host-election flicker replaying an unchanged incoming message does not
 * double-apply a non-idempotent effect (e.g. an item grant). */
export interface LogicEffectSyncMessage extends SyncMessageBase {
  kind: 'logic-effect';
  seq: number;
  effects: LogicEffect[];
}

/** Host-only: a periodic full resync of shared Logic state, covering a
 * client that joined mid-session or missed a `logic-effect` message - the
 * relay's own "latest state" item (`sstk-studio-infra`'s Phase 1 work,
 * `relay/src/index.js`) stores exactly this shape verbatim and hands it to
 * a newly-`$connect`ed client. `quests` carries each quest's `title`
 * alongside its status (not `QuestStatus` alone): `complete-quest`/
 * `fail-quest` actions never carry a title of their own (contract.ts), so
 * without it here a client applying this snapshot cold (a late joiner with
 * no earlier `start-quest` effect to have seeded one) would lose the
 * quest's display title entirely rather than merely not updating it. `items`
 * is the same idea for per-player item counts, added alongside
 * `variables`/`quests` so a late joiner's item state is caught up too,
 * rather than being silently absent from this resync mechanism the way it
 * previously was - keyed player id then item name. `seq` is the same
 * monotonic counter `LogicEffectSyncMessage.seq` documents. */
export interface LogicSnapshotSyncMessage extends SyncMessageBase {
  kind: 'logic-snapshot';
  seq: number;
  variables: Record<string, number>;
  quests: Record<string, { status: QuestStatus; title?: string }>;
  items: Record<string, Record<string, number>>;
  outcome: { result: 'win' | 'lose'; team?: string } | null;
}

export type SyncMessage = TransformSyncMessage | LogicEffectSyncMessage | LogicSnapshotSyncMessage;

/** Stamps a `TransformSyncMessage` with the current schema version - the
 * one place that version gets attached, so a future bump only has to
 * change `SYNC_MESSAGE_SCHEMA_VERSION` itself. */
export function transformSyncMessage(fields: Omit<TransformSyncMessage, 'v' | 'kind'>): TransformSyncMessage {
  return { kind: 'transform', v: SYNC_MESSAGE_SCHEMA_VERSION, ...fields };
}

export function logicEffectSyncMessage(effects: LogicEffect[], seq: number): LogicEffectSyncMessage {
  return { kind: 'logic-effect', v: SYNC_MESSAGE_SCHEMA_VERSION, seq, effects };
}

export function logicSnapshotSyncMessage(
  fields: Omit<LogicSnapshotSyncMessage, 'v' | 'kind'>,
): LogicSnapshotSyncMessage {
  return { kind: 'logic-snapshot', v: SYNC_MESSAGE_SCHEMA_VERSION, ...fields };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

// Plain `boolean` returns, not `value is X` type predicates: `Record<string,
// unknown>` has no index signature match with these named interfaces, so a
// predicate typed against them fails TS's "predicate type assignable to
// parameter type" check. `isSyncMessage` below stays the one exported
// `value is SyncMessage` predicate - narrowing `unknown` has no such
// mismatch, since every type is assignable to `unknown`.
function isTransformSyncMessage(value: Record<string, unknown>): boolean {
  return (
    typeof value.entityId === 'string' &&
    isFiniteNumber(value.seq) &&
    isFiniteNumber(value.x) &&
    isFiniteNumber(value.y) &&
    isFiniteNumber(value.z) &&
    isFiniteNumber(value.yaw)
  );
}

function isLogicEffectSyncMessage(value: Record<string, unknown>): boolean {
  // Structural check only, at the array level - not a deep per-effect
  // shape validation, the same "structural sanity check, not a deep schema
  // validation" line `layoutStorage.ts`'s own boundary parser draws.
  return isFiniteNumber(value.seq) && Array.isArray(value.effects);
}

function isQuestStatus(value: unknown): value is QuestStatus {
  return typeof value === 'string' && (QUEST_STATUSES as readonly string[]).includes(value);
}

/** True when every value of a plain object is a finite number - shared by
 * `variables` (flat) and each player's own item-count map inside `items`
 * (one level nested) below, the same rigour `isTransformSyncMessage` already
 * applies to every one of its own scalar fields. Previously `variables` was
 * checked only via `isPlainObject`, with no per-value check at all - a
 * string value (e.g. `{score: "5"}`) passed validation, was stored into
 * `evaluator.ts`'s `Map<string, number>`, and `add-variable`'s unguarded `+`
 * (`this.variable(name) + action.value`) then silently produced JS string
 * concatenation instead of addition. */
function isFiniteNumberMap(value: unknown): boolean {
  if (!isPlainObject(value)) return false;
  return Object.values(value).every(isFiniteNumber);
}

function isLogicSnapshotSyncMessage(value: Record<string, unknown>): boolean {
  if (!isFiniteNumber(value.seq)) return false;
  if (!isFiniteNumberMap(value.variables)) return false;
  if (!isPlainObject(value.quests)) return false;
  for (const quest of Object.values(value.quests)) {
    if (!isPlainObject(quest)) return false;
    if (!isQuestStatus(quest.status)) return false;
    if (quest.title !== undefined && typeof quest.title !== 'string') return false;
  }
  if (!isPlainObject(value.items)) return false;
  for (const owned of Object.values(value.items)) {
    if (!isFiniteNumberMap(owned)) return false;
  }
  const outcome = value.outcome;
  if (outcome !== null) {
    if (!isPlainObject(outcome)) return false;
    if (outcome.result !== 'win' && outcome.result !== 'lose') return false;
  }
  return true;
}

/** Structural boundary check for a `RelayMessage.payload` (or any other
 * `unknown` value read off the wire): true only for a payload shaped like a
 * `SyncMessage` at the version this client understands. Anything else -
 * wrong version, wrong/missing `kind`, a malformed field, or a payload sent
 * by something other than this protocol entirely (the relay's `payload` is
 * genuinely unconstrained, roadmap 2.1) - is rejected rather than guessed
 * at, the same "no saved layout" posture `layoutStorage.ts`'s
 * `isPersistedWorkspaceState` takes for a structurally-wrong persisted
 * value. */
export function isSyncMessage(value: unknown): value is SyncMessage {
  if (!isPlainObject(value)) return false;
  if (value.v !== SYNC_MESSAGE_SCHEMA_VERSION) return false;
  switch (value.kind) {
    case 'transform':
      return isTransformSyncMessage(value);
    case 'logic-effect':
      return isLogicEffectSyncMessage(value);
    case 'logic-snapshot':
      return isLogicSnapshotSyncMessage(value);
    default:
      return false;
  }
}
